import isEqual from 'lodash/isEqual';
import {Moment} from 'moment';

import {ReportName} from 'common/report/constants';
import {Report} from 'common/report/insights/models/report';
import {
  FilterClause as FilterClauseType,
  InsightsQueryResults,
  ShowClause as ShowClauseType,
} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams, InsightsResourceType} from 'common/types/reports/bookmark';
import {MetricSegment} from 'common/charts/visualization/multi-metric/types';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import type {MetricDefinition, MetricsRequest, MetricsResponse} from 'common/react-query/metrics/types';
import {getCurrentMoment} from 'common/util/date';
import {getMetricName} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import {bookmarkUrlsClient} from 'common/report/queries/app-api/bookmark-urls-client';
import {getReportUrl} from 'common/report/util';
import {
  getFinalDisplayOptions,
  getMetricFormattingDefaultsFromShowClause,
  metricFormatterFunctionForShowClause,
} from 'common/report/insights/charts/util';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {formatMetricNumberWithUnits, formatPercent} from 'common/util';
import {ClauseType, createMetricUnitLabeler, ShowClauseData} from 'common/report/insights/util/tooltip';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {isBehaviorShowClause, isFormulaShowClause, isSavedMetricShowClause} from 'common/report/insights/models/utils';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getBookmarkTimeClauseForDateRange} from 'common/report/insights/util/time';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {getDateRangeLabel} from 'common/widgets/date-range-picker-new/util';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {
  comparisonDateToBookmarkFormat,
  getTimeComparisonRangeText,
} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {TEXT_HIGHLIGHT_THEME} from 'common/components/text-highlight';
import {TooltipItemType, TooltipSection} from 'common/components/chart-tooltip/types';
import {TimeRangeAndComparison} from 'iron/common/report/canvas/types';

import {MetricNodeData} from './types';

export function buildMetricRequest({
  nodeData,
  asOfDate,
  dateRange,
  timeComparison,
  additionalFilters,
  projectId,
  workspaceId,
}: {
  nodeData: MetricNodeData;
  asOfDate?: string;
  dateRange: DateRange;
  timeComparison: null | ComparisonDate;
  additionalFilters: FilterClauseType[];
  projectId: string;
  workspaceId?: string;
}): MetricsRequest {
  let newDateRange = dateRange;
  if ((newDateRange.isRelativeWindow() || newDateRange.isRolling()) && asOfDate && asOfDate !== `today`) {
    const calendarDatesAsOf = newDateRange.getCalendarDatesAsOf(asOfDate);
    newDateRange = new DateRange({
      type: DateRangeType.Between,
      from: calendarDatesAsOf.from,
      to: calendarDatesAsOf.to,
    });
  }

  return {
    params: {
      globalDataGroupId: nodeData.metricDataGroupId,
      definition: {
        ...nodeData.metricDefinition,
        comparisons: timeComparison ? [comparisonDateToBookmarkFormat(timeComparison)] : [],
      },
      time: {
        ...getBookmarkTimeClauseForDateRange(new DateRange(newDateRange)),
        unit: TimeUnit.Day,
      },
      filters: [...nodeData.metricFilters, ...additionalFilters],
    },
    queryParams: {
      project_id: projectId!,
      ...(workspaceId ? {workspace_id: workspaceId} : {}),
    },
  };
}

export function convertToInsightsBookmarkParams({
  metricDefinition,
  metricFilters,
  metricResponse,
  additionalFilters,
  dateRange,
  dataGroupId,
  overrideChartType = ChartType.Metric,
}: {
  metricDefinition: ShowClauseType;
  metricFilters: FilterClauseType[];
  metricResponse: MetricsResponse;
  additionalFilters: FilterClauseType[];
  dateRange: DateRange;
  dataGroupId?: string;
  overrideChartType?: ChartType;
}): InsightsBookmarkParams {
  const report = Report.createDefaultReport();

  report.overrideChartType(overrideChartType);

  const bookmarkParams = report.toBookmarkData();
  bookmarkParams.sections.globalDataGroupId = dataGroupId;
  bookmarkParams.sections.show = [metricDefinition];
  bookmarkParams.sections.filter = [...metricFilters, ...additionalFilters];
  bookmarkParams.sections.time = [{...getBookmarkTimeClauseForDateRange(dateRange), unit: TimeUnit.Day}];
  const timeComparisonFromDate = metricResponse.comparisons[0].meta.date_range.from_date;
  bookmarkParams.timeComparison = {from_date: timeComparisonFromDate.slice(0, 10)};

  return bookmarkParams;
}

export function convertToInsightsQueryResults({
  metricsResponse,
}: {
  metricsResponse: MetricsResponse;
}): InsightsQueryResults {
  const metricName = metricsResponse.meta.metric_name;

  // current results
  const dateRange = metricsResponse.meta.date_range;
  const timeseriesTimestamps = metricsResponse.meta.timeseries_timestamps;
  const timePeriodSettings = metricsResponse.meta.time_period_settings;
  const retentionBucketsSize = metricsResponse.meta.retention_buckets_size;
  const timeseriesResults = metricsResponse.timeseries;
  const timestampSeries = Object.fromEntries(
    timeseriesTimestamps.map((timestamp, index) => [timestamp, timeseriesResults[index]]),
  );

  // past results
  const timeComparisonDateRange = metricsResponse.comparisons[0].meta.date_range;
  const timeComparisonTimeseriesTimestamp = metricsResponse.comparisons[0].meta.timeseries_timestamps;
  const timeComparisonTimeseriesResults = metricsResponse.comparisons[0].timeseries;
  const timeComparisonTimestampSeries = Object.fromEntries(
    timeComparisonTimeseriesTimestamp.map((timestamp, index) => [timestamp, timeComparisonTimeseriesResults[index]]),
  );

  return {
    computed_at: getCurrentMoment().toISOString(),
    headers: [`$event`],
    meta: {
      min_sampling_factor: 1,
      report_sections: {
        show: [
          {
            metric_key: metricName,
            apiResultsTruncated: false,
            ...(retentionBucketsSize ? {retention_buckets_size: retentionBucketsSize} : {}),
          },
        ],
        group: [],
      },
      time_period_settings: timePeriodSettings,
    },
    date_range: dateRange,
    series: {
      [metricName]: timestampSeries,
    },
    time_comparison: {
      date_range: timeComparisonDateRange,
      series: {
        [metricName]: timeComparisonTimestampSeries,
      },
    },
  };
}

export async function generateBookmarkUrl({
  metricDefinition,
  metricFilters,
  metricResponse,
  additionalFilters,
  dateRange,
  dataGroupId,
  projectId,
  workspaceId,
}: {
  metricDefinition: ShowClauseType;
  metricFilters: FilterClauseType[];
  metricResponse: MetricsResponse;
  additionalFilters: FilterClauseType[];
  dateRange: DateRange;
  dataGroupId?: string;
  projectId: number;
  workspaceId: number;
}) {
  const bookmarkParams = convertToInsightsBookmarkParams({
    metricDefinition,
    metricFilters,
    metricResponse,
    additionalFilters,
    dateRange,
    dataGroupId,
    overrideChartType: ChartType.Line,
  });
  const bookmarkUrlSlug = await bookmarkUrlsClient.getBookmarkUrlAsync({
    project_id: projectId,
    type: ReportName.Insights,
    params: bookmarkParams,
  });
  return getReportUrl(`insights#${bookmarkUrlSlug}`, {
    projectId,
    workspaceId,
  });
}

export function getMetricSegment({
  metricDefinition,
  dataGroup,
  metricResponse,
  hideUnit,
  i18n,
}: {
  metricDefinition: ShowClauseType;
  dataGroup?: DataGroup;
  metricResponse: MetricsResponse;
  hideUnit?: boolean;
  i18n: LocalizationStore;
}): MetricSegment {
  const metricClause: ShowClauseData = {
    ...metricDefinition,
    ...(isBehaviorShowClause(metricDefinition) && metricDefinition.behavior
      ? {
          ...metricDefinition.measurement,
          resourceType: metricDefinition.behavior.resourceType,
          dataGroupId: metricDefinition.behavior.dataGroupId,
        }
      : {resourceType: InsightsResourceType.Events}),
    clauseType: ClauseType.Show,
    dataGroup,
    metricKey: ``,
  };
  const metricUnitLabeler = createMetricUnitLabeler(metricClause);
  const metricUnit: Translatable = metricUnitLabeler ? metricUnitLabeler(0) : ``;

  return {
    value: metricResponse.aggregate,
    subLabel: !hideUnit ? i18n.print(metricUnit) : ``,
    comparisonValue: metricResponse?.comparisons?.[0]?.aggregate ?? 0,
  };
}

export function formatMetricValue({
  value,
  metricDefinition,
  metrics,
}: {
  value: number;
  metricDefinition: ShowClauseType;
  metrics: MetricsResource;
}): string {
  const formattedValue = metricFormatterFunctionForShowClause(metricDefinition)(value);
  const defaultOptions = getMetricFormattingDefaultsFromShowClause(metricDefinition);
  return formatMetricNumberWithUnits(formattedValue, {
    ...getFinalDisplayOptions(metricDefinition, eventPropertyDefinitionsStore, metrics, defaultOptions, {}),
  });
}

export function getMetricNodeTitle({
  metricName,
  metricDefinition,
  metrics,
}: {
  metricName: string;
  metricDefinition: MetricDefinition;
  metrics: MetricsResource;
}): string {
  // if metric name is defined, return metric name
  if (metricName) {
    return metricName;
  }

  // if metric name is not defined, metric node title should be the name of the selected metric
  if (metricDefinition) {
    if (isSavedMetricShowClause(metricDefinition) || isFormulaShowClause(metricDefinition)) {
      return (metricDefinition.id && metrics.getOne(metricDefinition.id)?.value?.name) || ``;
    }
    if (isBehaviorShowClause(metricDefinition) && metricDefinition.behavior) {
      return metricDefinition.behavior.renamed || getMetricName(metricDefinition.behavior);
    }
  }

  return ``;
}

export function computeMetricTooltipSections({
  metricName,
  metricUnit,
  formattedMetricValue,
  formattedComparisonValue,
  liftValue,
  timeRangeAndComparison,
  i18n,
}: {
  metricName: string;
  metricUnit?: string;
  formattedMetricValue: string;
  formattedComparisonValue: string;
  liftValue: null | number;
  timeRangeAndComparison: TimeRangeAndComparison;
  i18n: LocalizationStore;
}): TooltipSection[] {
  const {print} = i18n;

  const dateRangeLabel = print(
    getDateRangeLabel({
      dateRange: new DateRange(timeRangeAndComparison.timeRange),
      matchingPreset:
        FullLengthDateRange.getPresetOptions().find((preset) =>
          isEqual(preset.value, timeRangeAndComparison.timeRange),
        ) ?? null,
      projectAsOfDate: null,
      now: getCurrentMoment({localized: true}) as Moment,
    }),
  );

  const timeComparisonLabel = print(getTimeComparisonRangeText(timeRangeAndComparison.timeComparison));

  const sections: TooltipSection[] = [
    {
      rows: [
        {
          items: [
            {
              text: `${metricName || `New`}`,
              type: TooltipItemType.Strong,
            },
          ],
        },
        {
          items: [
            {
              text: `${dateRangeLabel} vs ${timeComparisonLabel}`,
              type: TooltipItemType.Detail,
            },
          ],
        },
        {
          items: [
            {
              text: formattedMetricValue,
              type: TooltipItemType.Data,
            },
            {
              text: metricUnit ?? ``,
              type: TooltipItemType.Text,
            },
          ],
        },
        ...(liftValue !== null
          ? [
              {
                items: [
                  {
                    text: formatPercent(Math.abs(liftValue)),
                    theme: liftValue < 0 ? TEXT_HIGHLIGHT_THEME.NEGATIVE : TEXT_HIGHLIGHT_THEME.POSITIVE,
                    type: TooltipItemType.Data,
                  },
                  {
                    text: i18n.t(`chart-actions.tooltip.from-previous-period`),
                    type: TooltipItemType.Text,
                  },
                ],
              },
            ]
          : []),
        {
          items: [
            {
              text: formattedComparisonValue,
              type: TooltipItemType.Data,
            },
            {
              text: `${timeComparisonLabel}`,
              type: TooltipItemType.Text,
            },
          ],
        },
      ],
    },
  ];
  return sections;
}
